import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { isAllowedMobileReturnScheme } from "./mobileReturnScheme";
import { MOBILE_RETURN_SCHEME_KEY, MOBILE_AUTH_TOKEN_KEY } from "./useMobileAppLogin";

export const useCompleteSSO = () => {
  const { loginWithToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasAttemptedRef = useRef(false);

  const token = searchParams.get("token") ?? "";

  const completeSSOmutation = useTellescopeMutation({
    model: "users",
    method: "complete_sso",
    autoCacheUpdateStrategy: "disabled",
    onSuccess: (result: {
      authToken: string;
      user: { businessId: string; organizationIds?: string[]; requiresMFA?: boolean };
    }) => {
      loginWithToken({ authToken: result.authToken, user: result.user });

      const rawScheme = searchParams.get("returnScheme");
      const returnScheme = rawScheme
        ? decodeURIComponent(rawScheme)
        : sessionStorage.getItem(MOBILE_RETURN_SCHEME_KEY) ?? "";
      const isMobileFlow = !!returnScheme && isAllowedMobileReturnScheme(returnScheme);

      if (result.user.requiresMFA) {
        if (isMobileFlow) {
          sessionStorage.setItem(MOBILE_RETURN_SCHEME_KEY, returnScheme);
          navigate("/mobile-app/mfa", { replace: true });
        } else {
          navigate("/mfa", { replace: true });
        }
        return;
      }

      if (isMobileFlow) {
        sessionStorage.setItem(MOBILE_RETURN_SCHEME_KEY, returnScheme);
        sessionStorage.setItem(MOBILE_AUTH_TOKEN_KEY, result.authToken);
        navigate("/mobile-app/success", { replace: true });
        return;
      }
      navigate("/dashboard", { replace: true });
    },
  });

  useEffect(() => {
    if (hasAttemptedRef.current || !token) return;
    hasAttemptedRef.current = true;

    completeSSOmutation.mutate({ token });
  }, [token, completeSSOmutation]);

  const error = !token ? "No SSO token provided" : completeSSOmutation.error?.message;

  return { isLoading: completeSSOmutation.isPending, error };
};
